/*
 * DS Piece Access
 * Gate for Atelier product pages.
 *
 * A Piece's credential is a real Shopify Page with an unguessable handle
 * (config.isPieceRoute, computed server-side in theme.liquid from
 * template.suffix — see that file). Reaching that page is a genuine
 * server-side check: Shopify's own routing 404s before Liquid runs if the
 * handle doesn't exist. Everything past that point — unlockAtelier(),
 * localStorage persistence, the product-page gate — is theme-only V1
 * scope: it shapes the experience but does not enforce a purchase. See
 * the purchase-controls removal below for what that specifically means.
 */
(function () {
  var STORAGE_KEY = 'ds_piece_access_atelier';
  var STORAGE_VALUE = 'granted';
  var config = window.DSPieceAccessConfig || {};
  var ACCESS_PATH = config.atelierAccessPath || '/?ds_piece_access=atelier';
  var ROOT_PATH = config.rootPath || '/';
  var RETURN_PARAM = 'return_to';
  var ACCESS_PARAM = 'ds_piece_access';
  var RESET_VALUE = 'reset';
  var LOCKED_CLASS = 'ds-piece-access-locked-atelier';
  var UNLOCKED_CLASS = 'ds-piece-access-atelier';
  var ATELIER_HOME_HASH = '#dsh-collections';
  // Typewriter reveal on the no-piece gate (product-heron-style.liquid),
  // triggered by FIND THE PIECE — see wireDiscoveryReveal(). Untouched by
  // the holder-sequence work below; HOLDER_TYPE_SPEED reuses this same
  // value on purpose, see the note further down.
  var DISCOVERY_TYPE_SPEED = 32; // ms per character
  var DISCOVERY_LINE_PAUSE = 380; // ms pause after a line finishes, before the next starts

  // Holder recognition sequence (runHolderSequence, below). Deliberately
  // built on the same character-reveal pace and skip philosophy as the
  // no-piece discovery reveal above, so the two experiences read as one
  // interface — see the comment on runHolderSequence for why this is a
  // parallel implementation rather than a literal shared function.
  var HOLDER_TYPE_SPEED = DISCOVERY_TYPE_SPEED; // same ms/character as the no-piece reveal
  var HOLDER_RECOGNISED_HOLD = 750; // ms to hold each non-final text beat before the next one
  var HOLDER_GRANTED_HOLD = 850; // ms to hold "ACCESS GRANTED" once typed, before handoff
  var HOLDER_EXIT_FADE = 560; // ms fade-out before handoff — matches --ds-piece-route-motion below

  function canUseStorage() {
    try {
      var testKey = STORAGE_KEY + '_test';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  var storageAvailable = canUseStorage();

  function isUnlocked() {
    if (!storageAvailable) return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === STORAGE_VALUE;
    } catch (e) {
      return false;
    }
  }

  function setHtmlState() {
    var unlocked = isUnlocked();
    document.documentElement.classList.toggle(UNLOCKED_CLASS, unlocked);
    document.documentElement.classList.toggle(LOCKED_CLASS, !unlocked);
    document.dispatchEvent(
      new CustomEvent('ds-piece-access:state', {
        detail: { area: 'atelier', unlocked: unlocked },
      })
    );
  }

  function unlockAtelier() {
    if (storageAvailable) {
      try {
        window.localStorage.setItem(STORAGE_KEY, STORAGE_VALUE);
      } catch (e) {}
    }
    setHtmlState();
  }

  function lockAtelier() {
    if (storageAvailable) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
    setHtmlState();
  }

  function sameOriginPath(value) {
    if (!value) return '';
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return '';
    }
  }

  function accessUrl(returnTo) {
    var url = new URL(ACCESS_PATH, window.location.origin);
    var safeReturn = sameOriginPath(returnTo);
    if (safeReturn) url.searchParams.set(RETURN_PARAM, safeReturn);
    return url.pathname + url.search;
  }

  function requestedReturnTo() {
    try {
      return sameOriginPath(new URLSearchParams(window.location.search).get(RETURN_PARAM));
    } catch (e) {
      return '';
    }
  }

  // Reads the Piece's public archive identifier (e.g. "PIECE / 001") from
  // the qr-landing section's own DOM — set via that section's settings in
  // Admin (see qr-landing.liquid), never from the URL. Deliberately not
  // sourced from anything credential-bearing: this is display copy for
  // the recognition beat in runHolderSequence(), nothing reads it to
  // decide access.
  function requestedPublicPieceId() {
    try {
      var landing = document.querySelector('[data-ds-piece-unlock="atelier"]');
      var value = landing ? landing.getAttribute('data-ds-piece-public-id') : '';
      return (value || '').trim();
    } catch (e) {
      return '';
    }
  }

  function defaultAtelierTarget() {
    return (ROOT_PATH || '/') + ATELIER_HOME_HASH;
  }

  function isMeaningfulReturnTo(returnTo) {
    if (!returnTo) return false;

    try {
      var url = new URL(returnTo, window.location.origin);
      var root = new URL(ROOT_PATH || '/', window.location.origin);
      var isRoot = url.pathname === root.pathname && !url.search && !url.hash;
      // A return_to pointing back at the Piece route we're currently on
      // would loop straight back into the recognition sequence — fall
      // through to the default Atelier target instead. This deliberately
      // doesn't try to know whether some OTHER arbitrary path is a Piece
      // route (only the server, via isPieceRoute, can know that) — it
      // only guards the one case that's actually reachable here.
      var isSelfAccessRoute = isAccessRoute() && url.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '');
      return !isRoot && !isSelfAccessRoute;
    } catch (e) {
      return false;
    }
  }

  function accessDestination(returnTo) {
    return isMeaningfulReturnTo(returnTo) ? returnTo : defaultAtelierTarget();
  }

  // The real credential check. isPieceRoute is computed server-side in
  // theme.liquid from template.suffix (a Piece is a real Shopify Page on
  // its own piece-* alternate template — see that file for why each
  // Piece needs its own template rather than sharing one) — Shopify's
  // own routing has already resolved page existence before this ever
  // runs, so this isn't trusting anything the client supplied. No
  // query-string param and no hardcoded path grant access here anymore;
  // see the module header for why that changed.
  function isAccessRoute() {
    return !!config.isPieceRoute;
  }

  function isResetRoute() {
    try {
      return new URLSearchParams(window.location.search).get(ACCESS_PARAM) === RESET_VALUE;
    } catch (e) {
      return false;
    }
  }

  function handleResetRoute() {
    if (!isResetRoute()) return false;

    lockAtelier();
    window.location.replace(ROOT_PATH);
    return true;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Holder recognition sequence: PIECE ID, RECOGNISED, ACCESS GRANTED,
  // then handoff through the qr-landing section's own DS monogram beneath
  // this layer. The text beats type into the SAME line element in place.
  // No ENTER ATELIER click — the Piece scan itself was the customer's
  // action, so this runs straight through to the destination.
  //
  // Access is granted (unlockAtelier) by the caller BEFORE this function
  // is even called — this function is presentation only, per the brief:
  // the Atelier permission must not depend on the animation completing.
  //
  // Reuses HOLDER_TYPE_SPEED (aliased to the no-piece reveal's own
  // DISCOVERY_TYPE_SPEED) and the same "type one character, checking a
  // skip flag every tick" shape as wireDiscoveryReveal()'s typeLine()
  // below, so both experiences share one typewriter feel. It's a parallel
  // implementation rather than a literally shared function on purpose:
  // the no-piece reveal types through a LIST of separate lines with a
  // pause between them (and, once skipped, cascades the rest of that list
  // synchronously with no pause at all — see typeLine()); this one retypes
  // ONE line with its own hold durations. Forcing both shapes
  // through a single shared function risked subtly changing the approved
  // no-piece skip behaviour for no real benefit, so instead this is new,
  // adjacent code — same constants, same rhythm, same skip philosophy,
  // verified separately.
  // publicId is the Piece's archive identifier (e.g. "PIECE / 001") — pure
  // display copy, sourced from the qr-landing section's own settings (see
  // requestedPublicPieceId()), never from the URL/handle. When present it
  // becomes a leading stage: the system naming what it found, before it
  // confirms recognition — not a second credential, not a password
  // prompt. Optional and per-page, so future Pieces (002, 003, ...) just
  // set their own section setting; nothing here hardcodes a number.
  function runHolderSequence(route, destination, publicId) {
    var line = route.querySelector('[data-ds-piece-signal-line]');
    var stages = publicId ? [publicId, 'RECOGNISED', 'ACCESS GRANTED'] : ['RECOGNISED', 'ACCESS GRANTED'];
    var liveTexts = publicId ? ['Piece identified.', 'Piece recognised.', 'Access granted.'] : ['Piece recognised.', 'Access granted.'];
    var timers = [];

    function clearTimers() {
      timers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
      timers = [];
    }

    function setLiveText(text) {
      var live = route.querySelector('[data-ds-piece-live]');
      if (live) live.textContent = text;
    }

    function finish() {
      if (route.getAttribute('data-sequence-complete') === 'true') return;

      clearTimers();
      route.setAttribute('data-sequence-complete', 'true');
      if (line) line.textContent = '';
      route.classList.add('is-logo-stage', 'is-recognised', 'is-granted');
      setLiveText('Atelier opening.');

      // Fade the clean black/logo stage out, then hand off. With reduced
      // motion, the CSS transition and JS delay both collapse.
      route.classList.add('is-exiting');
      var exitDelay = prefersReducedMotion() ? 0 : HOLDER_EXIT_FADE;
      timers.push(
        window.setTimeout(function () {
          window.location.href = destination;
        }, exitDelay)
      );
    }

    function typeStage(index) {
      if (!line || index >= stages.length) {
        finish();
        return;
      }

      var fullText = stages[index];
      var isLast = index === stages.length - 1;
      route.classList.add(isLast ? 'is-granted' : 'is-recognised');

      var charIndex = 0;
      (function step() {
        charIndex++;
        line.textContent = fullText.slice(0, charIndex);

        if (charIndex >= fullText.length) {
          setLiveText(liveTexts[index]);
          var hold = isLast ? HOLDER_GRANTED_HOLD : HOLDER_RECOGNISED_HOLD;
          timers.push(
            window.setTimeout(function () {
              if (isLast) {
                finish();
              } else {
                line.textContent = '';
                typeStage(index + 1);
              }
            }, hold)
          );
        } else {
          timers.push(window.setTimeout(step, HOLDER_TYPE_SPEED));
        }
      })();
    }

    // Tap-to-skip: jumps straight to the handoff and proceeds.
    // Access itself has already been granted before the animation begins.
    // Capture phase so it fires before anything else on whatever was
    // actually tapped.
    route.addEventListener(
      'click',
      function (event) {
        if (route.getAttribute('data-sequence-complete') === 'true') return;
        event.preventDefault();
        event.stopPropagation();
        finish();
      },
      true
    );

    if (prefersReducedMotion()) {
      finish();
      return;
    }

    typeStage(0);
  }

  function injectAccessRouteStyles() {
    if (document.getElementById('ds-piece-access-route-styles')) return;

    var style = document.createElement('style');
    style.id = 'ds-piece-access-route-styles';

    // Visual anatomy of the holder route:
    //   canvas — a clean black screen
    //   signal — one centered line, retyped in place for the three text beats
    //   handoff — the qr-landing section's own DS monogram appears underneath
    //             as this layer exits
    style.textContent = [
      'body.ds-piece-access-route-active { overflow: hidden; }',
      '.ds-piece-access-route, .ds-piece-access-route * { box-sizing: border-box; }',

      '.ds-piece-access-route { --ds-piece-route-motion: 560ms; position: fixed; inset: 0; z-index: 2147483000; min-height: 100svh; display: grid; place-items: center; padding: clamp(18px, 5vw, 56px); background: #000; color: #fff; font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif; text-align: center; cursor: pointer; overflow: hidden; opacity: 1; transition: opacity var(--ds-piece-route-motion) ease; }',
      '.ds-piece-access-route:focus { outline: none; }',
      '.ds-piece-access-route[data-sequence-complete="true"] { cursor: default; }',
      '.ds-piece-access-route.is-exiting { opacity: 0; pointer-events: none; }',

      '.ds-piece-access-route__inner { width: min(100%, 420px); min-height: 160px; display: grid; place-items: center; padding: 0; background: transparent; border: 0; outline: 0; box-shadow: none; }',

      // Signal: one line, always visible, retyped in place — no opacity-based
      // crossfade here, the typewriter itself (runHolderSequence) is the reveal.
      '.ds-piece-access-route__signal { grid-area: 1 / 1; position: relative; width: min(100%, 360px); min-height: 1.4em; display: flex; align-items: center; justify-content: center; opacity: 1; transition: opacity var(--ds-piece-route-motion) ease; }',
      '.ds-piece-access-route.is-logo-stage .ds-piece-access-route__signal { opacity: 0; }',
      '.ds-piece-access-route__line { margin: 0; min-height: 1.4em; color: #fff; font-family: inherit; font-size: 13px; font-weight: 400; letter-spacing: 0.14em; line-height: 1.4; text-transform: uppercase; }',
      '.ds-piece-access-route__live { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }',
      '@media (prefers-reduced-motion: reduce) { .ds-piece-access-route, .ds-piece-access-route * { transition-duration: 1ms !important; animation: none !important; } }',

      '@media (max-width: 600px) { .ds-piece-access-route { padding: 16px 16px max(16px, env(safe-area-inset-bottom)); } .ds-piece-access-route__inner { width: 100%; min-height: 140px; } }',
    ].join('\n');
    document.head.appendChild(style);
  }

  function renderAccessRoute() {
    if (!isAccessRoute()) return;

    document.body.classList.add('ds-piece-access-route-active');
    injectAccessRouteStyles();

    var existing = document.querySelector('[data-ds-piece-access-route]');
    if (existing) return;

    var returnTo = requestedReturnTo();
    var destination = accessDestination(returnTo);

    var route = document.createElement('section');
    route.className = 'ds-piece-access-route';
    route.setAttribute('data-ds-piece-access-route', '');
    route.setAttribute('data-sequence-complete', 'false');
    route.setAttribute('aria-label', 'Piece access recognition');
    route.setAttribute('role', 'dialog');
    route.setAttribute('aria-modal', 'true');
    route.setAttribute('tabindex', '-1');
    route.innerHTML =
      '<div class="ds-piece-access-route__inner">' +
        '<div class="ds-piece-access-route__signal" aria-hidden="true">' +
          '<h1 class="ds-piece-access-route__line" data-ds-piece-signal-line></h1>' +
        '</div>' +
        '<p class="ds-piece-access-route__live" data-ds-piece-live aria-live="polite" aria-atomic="true">Piece recognition started.</p>' +
      '</div>';

    document.body.insertBefore(route, document.body.firstChild);
    route.focus({ preventScroll: true });

    // Access is granted here, immediately — before any animation runs. The
    // sequence below is presentation only; it must never be a precondition
    // for the permission itself (see the accessibility note on
    // runHolderSequence). A tab closed mid-animation, a slow device, a
    // reduced-motion skip — none of it should be able to leave a genuine
    // Piece holder locked out.
    unlockAtelier();
    runHolderSequence(route, destination, requestedPublicPieceId());
  }

  function wireLockedAtelierLinks() {
    // Locked Atelier links resolve to the product page gate; no Piece has been presented yet.
  }

  function wireProductGate() {
    var gate = document.querySelector('[data-ds-piece-gate="atelier"]');
    if (!gate) return;

    syncProductGateState();

    var gateLinks = gate.querySelectorAll('[data-ds-piece-access-link]');
    gateLinks.forEach(function (link) {
      link.setAttribute('href', accessUrl(window.location.pathname + window.location.search));
    });

    // RETURN is a plain link to the homepage by default (works with no
    // JS); when there's actual browser history to go back to, prefer that
    // instead so it reads as "go back" rather than always landing on the
    // homepage regardless of where the visitor came from.
    var returnLink = gate.querySelector('[data-ds-piece-return]');
    if (returnLink && window.history.length > 1) {
      returnLink.addEventListener('click', function (event) {
        event.preventDefault();
        window.history.back();
      });
    }

    wireDiscoveryReveal(gate);
  }

  // FIND THE PIECE swaps the gate's default stage for a typewriter-revealed
  // discovery stage, in place — the mark above and Return below (outside
  // this function's concern entirely) never move. A real href to the shop
  // collections stays on the link the whole time, so it still works with
  // no JS; this only intercepts the click when script actually runs.
  function wireDiscoveryReveal(gate) {
    var findLink = gate.querySelector('[data-ds-piece-find]');
    var defaultStage = gate.querySelector('[data-ds-piece-stage="default"]');
    var discoveryStage = gate.querySelector('[data-ds-piece-stage="discovery"]');
    if (!findLink || !defaultStage || !discoveryStage) return;

    var lines = discoveryStage.querySelectorAll('[data-ds-piece-line]');
    var skipRequested = false;

    discoveryStage.addEventListener('click', function () {
      skipRequested = true;
    });

    function typeLine(index) {
      if (index >= lines.length) return;

      var line = lines[index];
      var fullText = line.getAttribute('data-full-text') || '';

      if (skipRequested) {
        line.textContent = fullText;
        typeLine(index + 1);
        return;
      }

      var charIndex = 0;
      (function step() {
        if (skipRequested) {
          line.textContent = fullText;
          window.setTimeout(function () {
            typeLine(index + 1);
          }, 0);
          return;
        }

        charIndex++;
        line.textContent = fullText.slice(0, charIndex);

        if (charIndex >= fullText.length) {
          window.setTimeout(function () {
            typeLine(index + 1);
          }, DISCOVERY_LINE_PAUSE);
        } else {
          window.setTimeout(step, DISCOVERY_TYPE_SPEED);
        }
      })();
    }

    findLink.addEventListener('click', function (event) {
      event.preventDefault();
      if (discoveryStage.getAttribute('data-revealed') === 'true') return;
      discoveryStage.setAttribute('data-revealed', 'true');

      defaultStage.hidden = true;
      discoveryStage.hidden = false;
      discoveryStage.focus({ preventScroll: true });

      if (prefersReducedMotion()) return; // lines already hold their full text

      lines.forEach(function (line) {
        line.setAttribute('data-full-text', line.textContent);
        line.textContent = '';
      });

      typeLine(0);
    });
  }

  function syncProductGateState() {
    var gate = document.querySelector('[data-ds-piece-gate="atelier"]');
    if (!gate) return;

    document.body.classList.toggle('ds-piece-product-gate-active', !isUnlocked());
  }

  // UX protection, not purchase security — see the module header. Liquid
  // has no visibility into localStorage, so it cannot withhold this
  // markup at render time; it's always present in the HTML response
  // regardless of lock state. What this does do is stop a locked
  // visitor's actual browser session from ever having the add-to-cart
  // form, variant id, or dynamic-checkout button in its live DOM — no
  // view-source-and-guess variant scraping from the rendered page, no
  // devtools Elements inspection turning it up. A raw HTTP request to
  // the same URL, or a direct /cart/add call with a known variant id,
  // is untouched by this and always was — that gap needs server-side
  // enforcement this V1 doesn't have (see the module header).
  function hidePurchaseControlsIfLocked() {
    if (isUnlocked()) return;
    var controls = document.querySelectorAll('[data-ds-piece-purchase-controls="atelier"]');
    controls.forEach(function (el) {
      el.remove();
    });
  }

  function wireQrLanding() {
    var landing = document.querySelector('[data-ds-piece-unlock="atelier"]');
    if (!landing) return;
    // The qr-landing section renders identically wherever it's placed —
    // the plain page.qr-landing.json template and a real piece-*
    // template both carry this same data attribute, so the DOM alone
    // can't tell a genuine Piece page from ordinary campaign content
    // reusing the section. isAccessRoute() can, server-verified: without
    // it, this button would still unlock on any page carrying the
    // section, piece or not.
    if (!isAccessRoute()) return;

    var enterLinks = landing.querySelectorAll('[data-action="enter"]');
    enterLinks.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var returnTo = requestedReturnTo();
        unlockAtelier();
        if (returnTo) {
          event.preventDefault();
          window.location.href = returnTo;
        }
      });
    });
  }

  function syncAtelierTabState() {
    var tabs = document.querySelectorAll('.dsh-tab[data-filter="atelier"]');
    var unlocked = isUnlocked();

    tabs.forEach(function (tab) {
      var originalLabel = tab.getAttribute('data-ds-piece-original-label');
      if (!originalLabel) {
        originalLabel = tab.textContent.trim();
        tab.setAttribute('data-ds-piece-original-label', originalLabel);
      }

      tab.textContent = unlocked ? originalLabel : originalLabel + ' [LOCKED]';
      tab.setAttribute('aria-label', unlocked ? originalLabel : originalLabel + ' locked');
    });
  }

  function openAtelierHashIfRequested() {
    if (!isUnlocked() || window.location.hash !== ATELIER_HOME_HASH) return;

    var wrap = document.getElementById('dsh-collections');
    if (!wrap) return;

    var tab = wrap.querySelector('.dsh-tab[data-filter="atelier"]');
    if (tab && !tab.classList.contains('is-active')) {
      tab.click();
    }
  }

  // Preview-only stress-test toggle. Lets you flip between "holder" and
  // "no piece" from wherever you're actually browsing — product pages,
  // homepage, mid-sequence — instead of hand-typing query-string URLs.
  // Gated on the theme's role, which Shopify sets per-request: it reads
  // "main" only on the currently PUBLISHED theme, so this can never render
  // for a real customer on the live site, on this draft/preview theme or
  // any future one, with no cleanup needed before going live.
  function isPreviewTheme() {
    try {
      return !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.role !== 'main');
    } catch (e) {
      return false;
    }
  }

  function injectDebugToggleStyles() {
    if (document.getElementById('ds-piece-access-debug-styles')) return;

    var style = document.createElement('style');
    style.id = 'ds-piece-access-debug-styles';
    style.textContent = [
      // Above the product gate for QA toggling, below the holder route so the
      // Piece recognition screen stays visually empty.
      '.ds-piece-debug { position: fixed; top: 12px; left: 12px; z-index: 2147482500; display: flex; flex-direction: column; gap: 8px; padding: 10px 12px; background: rgba(10,10,10,0.94); border: 1px solid rgba(255,255,255,0.22); color: #fff; font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif; }',
      '.ds-piece-debug__state { margin: 0; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.6; white-space: nowrap; }',
      '.ds-piece-debug__state b { color: #fff; opacity: 1; }',
      '.ds-piece-debug__row { display: flex; gap: 6px; }',
      '.ds-piece-debug button { appearance: none; background: transparent; border: 1px solid rgba(255,255,255,0.35); color: #fff; font: inherit; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; padding: 7px 9px; cursor: pointer; white-space: nowrap; }',
      '.ds-piece-debug button:hover { background: rgba(255,255,255,0.14); }',
      '.ds-piece-debug button:focus-visible { outline: 1px solid #fff; outline-offset: 2px; }',
      '@media (max-width: 600px) { .ds-piece-debug { top: auto; bottom: 12px; left: 12px; right: 12px; } }',
    ].join('\n');
    document.head.appendChild(style);
  }

  function renderDebugToggle() {
    if (!isPreviewTheme()) return;
    if (document.querySelector('[data-ds-piece-debug]')) return;

    injectDebugToggleStyles();

    var panel = document.createElement('div');
    panel.className = 'ds-piece-debug';
    panel.setAttribute('data-ds-piece-debug', '');
    panel.innerHTML =
      '<p class="ds-piece-debug__state">Piece access: <b data-ds-piece-debug-state>' +
        (isUnlocked() ? 'Holder' : 'No piece') +
      '</b></p>' +
      '<div class="ds-piece-debug__row">' +
        '<button type="button" data-ds-piece-debug-holder>I have the piece</button>' +
        '<button type="button" data-ds-piece-debug-reset>Reset</button>' +
      '</div>';

    // Grants directly rather than routing through a real Piece page —
    // there's no query-string route left that can do that (see
    // isAccessRoute()), and this panel only ever renders on a
    // preview/QA theme in the first place, so a shortcut here doesn't
    // weaken anything a real customer could reach.
    panel.querySelector('[data-ds-piece-debug-holder]').addEventListener('click', function () {
      unlockAtelier();
      window.location.href = accessDestination(requestedReturnTo());
    });

    panel.querySelector('[data-ds-piece-debug-reset]').addEventListener('click', function () {
      window.location.href = ROOT_PATH + '?ds_piece_access=reset';
    });

    document.addEventListener('ds-piece-access:state', function (event) {
      var label = panel.querySelector('[data-ds-piece-debug-state]');
      if (label) label.textContent = event.detail.unlocked ? 'Holder' : 'No piece';
    });

    document.body.appendChild(panel);
  }

  function init() {
    // Reset is a debug-only affordance (locks the current browser back
    // to "no piece") — gated the same way the debug panel is, so it's
    // impossible to trigger on a live/published theme even by hand-typing
    // the URL.
    if (isPreviewTheme() && handleResetRoute()) return;

    setHtmlState();
    renderAccessRoute();
    wireLockedAtelierLinks();
    wireProductGate();
    wireQrLanding();
    hidePurchaseControlsIfLocked();
    syncAtelierTabState();
    openAtelierHashIfRequested();
    renderDebugToggle();
  }

  document.addEventListener('ds-piece-access:state', syncAtelierTabState);
  document.addEventListener('ds-piece-access:state', syncProductGateState);

  window.DSPieceAccess = {
    unlockAtelier: unlockAtelier,
    lockAtelier: lockAtelier,
    isAtelierUnlocked: isUnlocked,
    atelierAccessUrl: accessUrl,
    atelierResetUrl: ROOT_PATH + '?ds_piece_access=reset',
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
